<template>
    <div class="afu-relative">
        <C1FormInput
            ref="input"
            v-model="keyword"
            :label="label"
            :label-for="labelFor"
            :placeholder="localPlaceholder"
            :size="size"
            :is-invalid="isInvalid"
            :invalid-feedback="invalidFeedback"
            :disabled="disabled"
            :show-x="!disabled"
            @input="onInput"
            @clear="clearAll"
            @focus="isFocused = true"
            @blur="onBlur"
            @keydown="onKeydown"
        />

        <div
            v-if="isFocused && !options.length && keyword.length >= searchMinLength && $slots['noResults']"
            class="afu-absolute afu-right-0 afu-mt-2 afu-w-full afu-rounded afu-shadow-lg afu-z-50"
        >
            <div class="afu-text-sm afu-p-3 afu-rounded afu-bg-white afu-shadow-xs afu-text-gray-600 afu-text-medium">
                <slot name="noResults" />
            </div>
        </div>

        <Transition
            enter-active-class="afu-transition afu-ease-out afu-duration-100"
            enter-class="afu-transform afu-opacity-0 afu-scale-95"
            enter-to-class="afu-transform afu-opacity-100 afu-scale-100"
            leave-active-class="afu-transition afu-ease-in afu-duration-75"
            leave-class="afu-transform afu-opacity-100 afu-scale-100"
            leave-to-class="afu-transform afu-opacity-0 afu-scale-95"
        >
            <div
                v-show="isFocused && (localJobCategories.length || (keyword.length >= searchMinLength && mutableOptions.length))"
                class="af-occupation-select-dropdown afu-absolute focus:afu-outline-none afu-right-0 afu-mt-2 afu-w-full afu-rounded afu-shadow-lg afu-z-50 afu-overflow-y-scroll"
                style="max-height: 500px;"
                tabindex="0"
            >
                <div class="afu-rounded afu-bg-white afu-shadow-xs">
                    <C1ClsSelectSearchList
                        ref="occupationSelectSearchList"
                        v-if="(keyword.length >= searchMinLength) && !searchLoading"
                        :options="mutableOptions"
                        :arrow-counter.sync="arrowCounter"
                        @select="onSelect"
                    />

                    <C1ClsSelectClsList
                        v-else
                        :job-categories="localJobCategories"
                        @selectOccupation="selectOccupation"
                    />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
    import C1FormInput from './C1FormInput';
    import C1ClsSelectClsList from './C1ClsSelectClsList';
    import C1ClsSelectSearchList from './C1ClsSelectSearchList';
    import debounce from 'lodash/debounce';

    export default {
        name: 'C1ClsSelect',

        components: {
            C1ClsSelectClsList,
            C1ClsSelectSearchList,
            C1FormInput,
        },

        props: {
            value: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                default: '',
            },

            labelFor: {
                type: String,
                default: '',
            },

            options: {
                type: Array,
                default: () => [],
            },

            searchMinLength: {
                type: Number,
                default: 3,
            },

            searchLoading: {
                type: Boolean,
                default: false,
            },

            size: {
                type: String,
                default: null,
                validator: value => ['sm', 'lg'].includes(value),
            },

            isInvalid: {
                type: Boolean,
                default: false,
            },

            invalidFeedback: {
                type: [String, Boolean],
                default: false,
            },

            placeholder: {
                type: String,
                default: '',
            },

            jobCategories: {
                type: Array,
                default: () => [],
            },

            selectedOccupations: {
                type: [Object, Array],
                default: null,
            },

            isSingleSelect: {
                type: Boolean,
                default: false,
            },

            disabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: this._uid,
                isFocused: false,
                keyword: '',
                localValue: [],
                arrowCounter: 0,
                localJobCategories: [],
                localSelectedOccupations: [],
                originalOptions: [],
                mutableOptions: [],
                defaultFilter: 'index',
            };
        },

        computed: {
            localPlaceholder() {
                return this.placeholder;
            },
        },

        watch: {
            options() {
                this.cloneOptions();
            },

            selectedOccupations() {
                this.cloneSelectedOccupations();
                this.cloneJobCategories();
                this.cloneOptions();
            },

            jobCategories() {
                this.cloneJobCategories();
            },

            value(value) {
                this.keyword = value;
            },
        },

        created() {
            this.keyword = this.value;
            this.cloneSelectedOccupations();
            this.cloneJobCategories();
            this.cloneOptions();
        },

        methods: {
            selectOccupation({ category, occupation }) {
                const originalOccupation = JSON.parse(JSON.stringify(occupation));
                delete originalOccupation.is_selected;

                if (this.isSingleSelect) {
                    category.occupations.forEach(o => {
                        o.is_selected = o.id === occupation.id;
                    });

                    this.keyword = originalOccupation.name;
                    this.localSelectedOccupations = originalOccupation;
                } else {
                    occupation.is_selected = true;
                    const idx = this.localSelectedOccupations.findIndex(
                        o =>
                            o.id === originalOccupation.id &&
                            o.category_id === originalOccupation.category_id,
                    );

                    if (idx >= 0) {
                        // this.localSelectedOccupations.splice(idx, 1);
                    } else {
                        this.localSelectedOccupations.push(originalOccupation);
                        this.attach(originalOccupation);
                    }

                    this.resetKeyword();
                }

                this.emitInput();
                this.resetArrowCounter();
                this.resetOptions();
                this.loseInputFocus();
                this.hit();
            },

            onSelect() {
                const selected = this.mutableOptions[this.arrowCounter];
                const selectedOption = this.options.find(
                    o =>
                        o.occupation_id === selected.occupation_id &&
                        o.category_id === selected.category_id,
                );

                if (selectedOption) {
                    const originalOccupation = {
                        id: selectedOption.occupation_id,
                        name: selectedOption.occupation_name,
                        category_id: selectedOption.category_id,
                        is_master: true,
                    };

                    if (this.isSingleSelect) {
                        this.keyword = originalOccupation.name;
                        this.localSelectedOccupations = originalOccupation;
                    } else {
                        const idx = this.localSelectedOccupations.findIndex(
                            o =>
                                o.id === selectedOption.occupation_id &&
                                o.category_id === selectedOption.category_id,
                        );

                        if (idx >= 0) {
                            // this.localSelectedOccupations.splice(idx, 1);
                        } else {
                            this.localSelectedOccupations.push(originalOccupation);
                            this.attach(originalOccupation);
                        }

                        this.resetKeyword();
                    }

                    this.emitInput();
                    this.resetArrowCounter();
                    this.resetOptions();
                    this.loseInputFocus();
                    this.hit();
                }
            },

            onInput: debounce(async function() {
                const keyword = this.keyword;
                this.emitInput();

                if (keyword.length >= this.searchMinLength) {
                    this.$emit('search', this.keyword);
                } else {
                    this.resetOptions();
                }
            }, 400),

            cloneJobCategories() {
                if (!this.jobCategories.length) {
                    return;
                }

                this.localJobCategories = JSON.parse(
                    JSON.stringify(this.jobCategories),
                ).map(category => ({
                    ...category,
                    is_open: false,
                    occupations: category.occupations.map(occupation => {
                        let isSelect;
                        if (!this.isSingleSelect) {
                            isSelect =
                                this.localSelectedOccupations.findIndex(
                                    i =>
                                        i.id === occupation.id &&
                                        i.category_id === occupation.category_id,
                                ) >= 0;
                        } else {
                            isSelect = false;
                        }

                        return {
                            ...occupation,
                            is_selected: isSelect,
                        };
                    }),
                }));

                this.localJobCategories.forEach(o => {
                    o.is_open = !!o.occupations.filter(o => o.is_selected).length;
                });
            },

            cloneOptions() {
                this.originalOptions = JSON.parse(JSON.stringify(this.options));
                this.mutableOptions = JSON.parse(JSON.stringify(this.options));
            },

            cloneSelectedOccupations() {
                if (
                    this.isSingleSelect ||
                    this.selectedOccupations === null ||
                    (Array.isArray(this.selectedOccupations) &&
                        !this.selectedOccupations.length) ||
                    (typeof this.selectedOccupations === 'object' &&
                        !Object.keys(this.selectedOccupations).length)
                ) {
                    return;
                }

                this.localSelectedOccupations = JSON.parse(
                    JSON.stringify(this.selectedOccupations),
                );
            },

            onBlur(evt) {
                const tgt = evt.relatedTarget;
                if (
                    tgt &&
                    (tgt.classList.contains('af-occupation-select-item') ||
                        tgt.classList.contains('af-occupation-select-dropdown'))
                ) {
                    this.$refs.input.$refs.input.focus();
                    return;
                }

                this.$emit('blur', evt);

                this.loseInputFocus();
            },

            onKeydown(evt) {
                const code = evt.code;

                if (code === 'Escape') {
                    this.onEsc();
                }

                if (!this.mutableOptions.length) {
                    return;
                }

                switch (code) {
                    case 'ArrowUp':
                        evt.preventDefault();
                        this.onArrowUp();
                        break;
                    case 'ArrowDown':
                        evt.preventDefault();
                        this.onArrowDown();
                        break;
                    case 'Enter':
                        this.onSelect();
                        break;
                }
            },

            onEsc() {
                this.loseInputFocus();
                this.resetArrowCounter();
            },

            onArrowDown() {
                if (this.arrowCounter < this.mutableOptions.length - 1) {
                    this.arrowCounter += 1;
                }
                this.fixScrolling();
            },

            onArrowUp() {
                if (this.arrowCounter > 0) {
                    this.arrowCounter -= 1;
                }
                this.fixScrolling();
            },

            fixScrolling() {
                const ref = `option_${this.arrowCounter}`;
                const element = this.$refs.occupationSelectSearchList.$refs[ref][0];
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            },

            resetArrowCounter() {
                this.arrowCounter = 0;
            },

            emitInput() {
                this.$emit('input', this.keyword);
            },

            hit() {
                this.$emit('hit', this.localSelectedOccupations);
            },

            attach(occupation) {
                this.$emit('attach', occupation);
            },

            loseInputFocus() {
                this.$refs.input.$refs.input.blur();
                this.isFocused = false;
            },

            resetOptions() {
                this.originalOptions = [];
                this.mutableOptions = [];
            },

            resetKeyword() {
                this.keyword = '';
                this.emitInput();
            },

            resetSelectedOccupations() {
                if (this.isSingleSelect) {
                    this.localJobCategories.filter(category => {
                        category.is_open = false;
                        category.occupations.filter(occupation => {
                            occupation.is_selected = false;
                        });
                    });
                }
            },

            clearAll() {
                this.resetKeyword();
                this.resetOptions();
                this.resetSelectedOccupations();
                this.loseInputFocus();
            },
        },
    };
</script>
